import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import {
  ArrowRightIcon,
  DownloadIcon,
  SearchIcon,
  ShareIcon,
  ViewListIcon
} from '@heroicons/react/solid'
import { DomoreportWalkthrough } from '@components/DomoreportWalkthrough'
import { BullseyeIcon } from '@components/UI/Icons/bullseye'

export default function GetDomoreport() {
  return (
    <>
      <NextSeo
        title="Domoreport - Get Next Day Home Repair Prices"
        description="Get prices for all your home repairs & prioritize your most urgent ones. Next day delivery. Get started today."
        canonical="https://majordomo.com/features/domoreport-repair-pricing"
        openGraph={{
          url: 'https://majordomo.com/domoreport-repair-pricing',
          title: 'Domoreport - Get Next Day Home Repair Prices | Majordomo',
          description:
            'Get prices for all your home repairs & prioritize your most urgent ones. Next day delivery. Get started today.',
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/domoreport-hero.png',
              width: 700,
              height: 681,
              alt: 'Person looking at a Domoreport on a laptop computer'
            },
            {
              url: 'https://majordomo.com/images/product-pages/domoreport.png',
              width: 1816,
              height: 1992,
              alt: 'Screen shot of Majordomo’s Domoreport'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <h1 className="md:pb-16 pb-4 md:px-0 px-8 text-brand text-center mx-auto md:text-4xl text-2xl font-black tracking-tight md:max-w-xl max-w-md uppercase">
          HOME INSPECTIONS ANALYZED, PRIORITIZED & COST-ESTIMATED ALL IN 24
          HOURS OR LESS
        </h1>

        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <ViewListIcon
                width={36}
                height={36}
                className="pr-2 mr-2 border-r border-gray-200 text-brand"
              />
              <h4 className="text-2xl font-black tracking-wide text-brand uppercase">
                Domoreport
              </h4>
            </div>

            <p className="lg:mb-8 md:mb-4 mb-8 xl:text-3xl lg:text-2xl md:text-lg text-xl font-light font-heading md:w-2/3">
              <span className="font-black">Take fast action</span> after the
              home inspection
            </p>

            <p className="w-full max-w-md md:p-0 px-8 mb-8">
              The Domoreport is the second half of the home inspection. It's the
              most in-depth, easy-to-read report on the house's condition along
              with a list of repair cost estimates identified from the
              inspection. It's the information you need to be able to negotiate
              with confidence -{' '}
              <span className="font-black">
                all delivered within 24 hours and with guaranteed satisfaction
              </span>
              .
            </p>

            <Link href="/checkout">
              <a className="flex items-center justify-center w-full">
                <Button variant="primary" clipSide="right">
                  Get repair estimates{' '}
                  <ArrowRightIcon width={18} height={18} className="ml-2" />
                </Button>
              </a>
            </Link>
          </div>

          <div className="relative md:mb-0 mb-8 md:w-full w-1/2 z-0">
            <img
              src="/images/product-pages/domoreport-hero.png"
              className="md:order-last order-first w-full"
            />
          </div>
        </div>

        <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 pb-12">
          Featured In:
        </h4>

        <div className="flex lg:flex-row flex-col justify-evenly lg:pb-36 pb-12">
          <Link href="https://rismedia.com/2020/01/16/disruptor-roundup-majordomo-home-inspections/">
            <a className="flex flex-col items-center justify-center bg-black p-8 w-full cursor-pointer">
              <div className="flex flex-col items-center justify-center space-y-4">
                <img
                  src="/images/product-pages/RIS_logo.png"
                  alt="RIS Logo"
                  width="180px"
                />
                <p className="text-center text-white">
                  Majordomo looks to deliver clarity to home inspections
                </p>
              </div>
            </a>
          </Link>

          <Link href="/blog/10-tech-products-inman-loved-in-2019/">
            <a className="flex flex-col items-center justify-center bg-accentBlue p-8 w-full cursor-pointer">
              <div className="flex flex-col items-center justify-center space-y-4">
                <img
                  src="/images/product-pages/inman-logo.svg"
                  alt="inman logo"
                  width="180px"
                />
                <p className="text-center text-white">
                  Top 10 tech product in 2019
                </p>
              </div>
            </a>
          </Link>
        </div>

        <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 p-12 pt-0 max-w-3xl mx-auto">
          How it works, in three easy steps
        </h4>

        <div className="flex lg:flex-row flex-col justify-center lg:items-stretch items-center lg:space-x-16 lg:space-y-0 space-y-8 sm:px-0 px-8 lg:pb-36 pb-12">
          <div className="relative bg-gray-50 p-8 pb-16 folded-r-5 lg:w-1/3 w-full max-w-sm">
            <h4 className="mb-4 text-lg font-black tracking-tight md:text-2xl w-52">
              1. Upload the home inspection
            </h4>
            <p>
              Once you receive the home inspection, you or the buyer simply
              uploads a copy of the PDF, and we'll get to work!
            </p>
          </div>

          <div className="relative w-full max-w-sm p-8 pb-16 bg-gray-50 folded-r-5 lg:w-1/3">
            <h4 className="mb-4 text-lg font-black tracking-tight md:text-2xl w-52">
              2. Majordomo analyses everything
            </h4>
            <p>
              A human - not an algorithm - reviews your home inspection. Repairs
              are researched to get local cost estimates. We compile all the
              details into a Domoreport and deliver it to you within 24 hours.
            </p>
          </div>

          <div className="relative w-full max-w-sm p-8 pb-16 bg-gray-50 folded-r-5 lg:w-1/3">
            <h4 className="mb-4 text-lg font-black tracking-tight md:text-2xl w-52">
              3. Take action with the Domoreport
            </h4>
            <p>
              Use the Domoreport's estimates, reports, and request list to take
              action on the property. Increase your chances for successful
              negotiations or prepare your home for sale.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center w-full px-8 pb-12 lg:pb-36 lg:p-0">
          <iframe
            width="840"
            height="472.4"
            src="https://www.youtube.com/embed/aFdisXTJ6Ms?hl=en_US&amp;rel=0&amp;autohide=1&amp;autoplay=0"
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media"
            title="Introducing Majordomo's Domoreport"
            className="hidden rounded lg:inline shadow-sm"
            allowFullScreen
          />

          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/aFdisXTJ6Ms?hl=en_US&amp;rel=0&amp;autohide=1&amp;autoplay=0"
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media"
            title="Introducing Majordomo's Domoreport"
            className="inline rounded lg:hidden shadow-sm"
            allowFullScreen
          />
        </div>

        <div className="mb-12 lg:mb-36">
          <div className="flex flex-col px-4 xl:flex-row justify-evenly lg:px-16">
            <div className="flex items-center justify-center w-full xl:w-1/2 lg:p-16">
              <img
                src="/images/product-pages/domoreport.png"
                alt="Majordomo product samples"
                className="max-w-sm xl:max-w-lg md:max-w-md"
              />
            </div>

            <div className="flex flex-col items-center w-full p-4 space-y-12 xl:p-16 xl:w-1/2">
              <div className="relative flex">
                <SearchIcon
                  width={56}
                  height={56}
                  className="absolute top-0 w-12 h-12 p-2 text-white xl:-left-24 md:w-16 md:h-16 bg-gold-400"
                />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl xl:mt-0 md:mt-20">
                    Defects identified and prioritized
                  </h4>
                  <p className="max-w-lg mr-auto">
                    At the core of the Domoreport is a list of repairs based on
                    defects identified in the inspection. We tell you where the
                    repairs need to happen, prioritize them, estimate costs, and
                    identify the type of contractors who could do the work – all
                    linked to the page number of the home-inspection report.
                  </p>
                </div>
              </div>

              <div className="relative flex">
                <BullseyeIcon className="absolute top-0 w-12 h-12 p-2 text-white fill-current xl:-left-24 md:w-16 md:h-16 bg-gold-400" />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl xl:mt-0 md:mt-20">
                    Precise local estimates, reviewed by humans
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Majordomo's cost estimates are based on more than 30 years
                    of research on 20,000 project estimates from 35,000
                    providers of materials, equipment, and labor across the US.
                    Every Domoreport is reviewed by an industry expert before
                    it's sent to you.{' '}
                    <a
                      href="/blog/majordomos-repair-cost-estimates-are-local-and-accurate/"
                      className="text-accentBlue hover:underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="relative flex">
                <DownloadIcon
                  width={56}
                  height={56}
                  className="absolute top-0 w-12 h-12 p-2 text-white xl:-left-24 md:w-16 md:h-16 bg-gold-400"
                />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl xl:mt-0 md:mt-20">
                    Simple request list export
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Build a custom request list of repair requests, credit
                    amounts, and items accepted as-is. Export a
                    professional-looking addendum attachment to use when
                    negotiating with sellers.{' '}
                    <a
                      href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list"
                      className="text-accentBlue hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="relative flex">
                <ShareIcon
                  width={56}
                  height={56}
                  className="absolute top-0 w-12 h-12 p-2 text-white xl:-left-24 md:w-16 md:h-16 bg-gold-400"
                />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl xl:mt-0 md:mt-20">
                    Easy to share
                  </h4>
                  <p className="max-w-lg mr-auto">
                    You get built-in tools to easily share the Domoreport and
                    collaborate with buyers during the inspection review period.
                  </p>
                </div>
              </div>

              <div className="relative flex ml-0 xl:self-start 2xl:ml-8 lg:ml-0 md:ml-12 sm:ml-8">
                <h4 className="max-w-lg mx-auto mb-2 text-lg font-black tracking-tight md:text-2xl">
                  All for just $99
                  <span className="text-base font-light"> / per report</span>
                </h4>
              </div>

              <div>
                <Link href="/checkout">
                  <a>
                    <Button variant="primary" clipSide="right">
                      Get repair estimates{' '}
                      <ArrowRightIcon width={18} height={18} className="ml-2" />
                    </Button>
                  </a>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col px-8 py-12 mb-12 bg-gray-50 sm:px-0 md:mb-36">
          <div className="flex flex-col items-center xl:flex-row">
            <div className="flex flex-col items-center justify-center w-full lg:w-1/2">
              <h4 className="max-w-lg mb-8 text-2xl font-black tracking-tight text-center">
                "Mind-blowingly accurate"
              </h4>

              <p className="max-w-sm mb-8 text-center xl:mb-0">
                "I was afraid this would be the 'online estimate of home
                repairs' which is never really accurate, but it's actually been
                really helpful."
              </p>
            </div>

            <div className="flex items-center justify-center w-full lg:w-1/2">
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/qRihffolpHs?hl=en_US&amp;rel=0&amp;autohide=1&amp;autoplay=0"
                frameBorder="0"
                className="rounded shadow-sm"
                allow="accelerometer; autoplay; encrypted-media"
                title="Introducing Majordomo's Domoreport"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        <DomoreportWalkthrough />

        <div className="flex flex-col items-center pb-12 lg:pb-36">
          <a href="/demo" target="_blank" rel="noopener noreferrer">
            <Button variant="primary" clipSide="right">
              View a sample Domoreport{' '}
              <ArrowRightIcon width={18} height={18} className="ml-2" />
            </Button>
          </a>
        </div>

        <div className="flex flex-col pb-12 lg:flex-row justify-evenly lg:pb-36">
          <div className="flex flex-col justify-center p-8 mt-0 text-gray-300 bg-black align-center lg:mt-32 lg:w-1/2 lg:pb-32 md:p-16 space-y-8">
            <h4 className="text-xl font-black tracking-tight text-white md:text-3xl">
              Get these free extras with your Domoreport
            </h4>

            <div>
              <h4 className="mb-2 text-lg font-black tracking-tight text-white md:text-2xl">
                Additional resources
              </h4>

              <p className="max-w-lg">
                Explore: We offer a full page of online resources, specific to
                your local area, to help buyers. The information includes
                homeowner rebates, permits, maps, utilities and much more{' '}
                <Link href="/features/explore-homeowner-resources">
                  <a className="text-accentBlue hover:underline">Learn more</a>
                </Link>
              </p>
            </div>

            <p className="max-w-lg">
              Documents: Save your home inspection report, photos and other
              important documents to Majordomo Documents and access them from
              any web browser; this service is free forever with every
              Domoreport.{' '}
              <Link href="/features/upload-property-documents">
                <a className="text-accentBlue hover:underline">Learn more</a>
              </Link>
            </p>

            <div>
              <h4 className="mb-2 text-lg font-black tracking-tight text-white md:text-2xl">
                24-hour access
              </h4>

              <p className="max-w-lg">
                Access Majordomo on your computer, smartphone or tablet anytime.
                Have a question? Majordomo is ready to help with 24-hour chat
                and customer support.
              </p>

              <div className="flex items-center justify-center mt-12">
                <Link href="/checkout">
                  <a className="text-black">
                    <Button variant="primary" clipSide="right">
                      Get repair estimates{' '}
                      <ArrowRightIcon width={18} height={18} className="ml-2" />
                    </Button>
                  </a>
                </Link>
              </div>
            </div>
          </div>

          <div className="hidden w-full bg-left-bottom bg-no-repeat bg-on-your-team lg:flex folded-l-max lg:w-1/2" />
        </div>

        <div className="mb-12 lg:mb-36">
          <div className="flex flex-col px-4 lg:flex-row justify-evenly lg:px-16">
            <div className="flex flex-col order-last w-full p-4 space-y-4 lg:p-16 lg:w-1/2 md:order-first">
              <div>
                <h4 className="text-lg font-black tracking-tight md:text-2xl">
                  100% satisfaction
                </h4>
                <h4 className="text-lg font-black tracking-tight md:text-2xl">
                  60-day money-back guarantee
                </h4>
              </div>

              <p>
                If you don't love the Domoreport, we'll return 100% of your
                money. But we're confident you'll love it. Here’s why:
              </p>

              <p>
                Majordomo's repair-estimate data comes from analysts who study
                contractor and insurance invoices for 20,000 projects from
                35,000 providers for material, equipment, labor and installation
                costs across the US. This lets us create highly accurate
                localized estimates for your specific market.{' '}
                <a
                  href="/blog/majordomos-repair-cost-estimates-are-local-and-accurate/"
                  className="text-accentBlue hover:underline"
                >
                  Learn More
                </a>
              </p>

              <p>
                We invite you to use the Domoreport with your home inspection
                and receive repair estimates within 24 hours. We're confident
                you'll be impressed with its completeness, readability and
                accuracy.
              </p>

              <p>
                So here's our offer: If you don’t love the Domoreport or if the
                estimates aren't accurate, we'll refund 100% of your money. You
                have nothing to lose by giving it a try.
              </p>
            </div>

            <div className="flex items-center justify-center order-first w-full lg:w-1/2 lg:p-16 md:order-last">
              <img
                src="/images/product-pages/satisfaction.png"
                className="max-w-xs md:max-w-md"
              />
            </div>
          </div>

          <div className="flex flex-col items-center justify-center w-full pt-8 pb-0 md:flex-row md:space-y-0 space-y-4 md:pb-12 md:pt-0">
            <Link href="/checkout">
              <a>
                <Button variant="primary" clipSide="right">
                  Get repair estimates{' '}
                  <ArrowRightIcon width={18} height={18} className="ml-2" />
                </Button>
              </a>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
